// src/pages/Participants.js - 참여자 선별기 페이지

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faBalanceScale, 
  faArrowLeft, 
  faFileInvoice, 
  faCheckCircle, 
  faExclamationTriangle, 
  faQuestionCircle, 
  faWandMagicSparkles, 
  faChartBar, 
  faChevronDown, 
  faChevronUp,
  faChevronLeft,
  faUpload,
  faUsers,
  faCog,
  faPlay,
  faFloppyDisk
} from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../context/AppContext';
import { getStudyArtifacts, optimizeSchedule, finalizeParticipants, saveSchedule } from '../services/api';
import { apiFetch } from '../services/apiClient';
import WorkspaceLayout from '../components/WorkspaceLayout';
import FeedbackButton from '../components/FeedbackButton';
import { Icon24, Icon36, Toast } from '../components/ui';
import { usePageHeader } from '../context/PageHeaderContext';
import './Participants.scss';

// 내부 시각화 컴포넌트들
const SimpleCalendar = ({ participants, scheduleColumns, nameColumn, hasNameColumn, targetGroups, updateTrigger, studyId, onScheduleReady }) => {
  const [optimizedSchedule, setOptimizedSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationData, setValidationData] = useState(null);

  const resolveParticipantName = React.useCallback((participant) => {
    if (!participant || typeof participant !== 'object') return '';

    // ✨ 1순위: 백엔드에서 명시적으로 설정한 _display_name 사용
    if (participant._display_name) {
      const displayName = String(participant._display_name).trim();
      if (displayName) return displayName;
    }

    // 2순위: name_column 사용 (hasNameColumn이 true일 때)
    const rawName = nameColumn ? participant[nameColumn] : undefined;
    const nameFromColumn = rawName !== undefined && rawName !== null
      ? String(rawName).trim()
      : '';

    if (hasNameColumn && nameFromColumn) {
      return nameFromColumn;
    }

    if (nameFromColumn) return nameFromColumn;

    // 3순위: 다른 이름 관련 필드들
    if (participant.participant_name) {
      return String(participant.participant_name).trim();
    }

    // 최종 fallback: ID 필드들
    if (participant.id) {
      return String(participant.id).trim();
    }

    if (participant.participant_id) {
      return String(participant.participant_id).trim();
    }

    return '';
  }, [nameColumn, hasNameColumn]);
  
  // AI 최적화 API 호출 (updateTrigger가 변경될 때만 실행)
  useEffect(() => {
    if (participants.length === 0 || !scheduleColumns || scheduleColumns.length === 0) {
      return;
    }
    
    // updateTrigger가 0이면 일정 최적화하지 않음 (버튼 클릭 시에만 업데이트)
    if (updateTrigger === 0 || updateTrigger === undefined) {
      setOptimizedSchedule(null); // 기존 일정 초기화
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    optimizeSchedule(participants, scheduleColumns, nameColumn, targetGroups, studyId, hasNameColumn)
      .then((result) => {
        if (result.success && result.optimized_schedule) {
          setOptimizedSchedule(result.optimized_schedule);
          
          // validation 정보가 있으면 경고 표시
          if (result.validation) {
            setValidationData(result.validation);
            const { total_participants, assigned_count, unassigned_count, missing_count, unassigned_participants, missing_participants } = result.validation;
            if (unassigned_count > 0 || missing_count > 0) {
              console.warn(`⚠️ 일정 최적화 경고: 전체 ${total_participants}명 중 ${assigned_count}명 배정, ${unassigned_count}명 미배정, ${missing_count}명 누락`);
              
              // 상세한 에러 메시지 생성
              let errorMessage = `일부 참여자(${unassigned_count + missing_count}명)가 일정에 배정되지 않았습니다.\n\n`;
              
              if (unassigned_count > 0 && unassigned_participants?.length > 0) {
                errorMessage += `[일정 조율 불가] ${unassigned_participants.length}명:\n`;
                errorMessage += `${unassigned_participants.join(', ')}\n`;
                errorMessage += `→ 가능한 일정이 다른 참여자들과 맞지 않아 배정되지 않았습니다.\n\n`;
              }
              
              if (missing_count > 0 && missing_participants?.length > 0) {
                errorMessage += `[일정 누락] ${missing_participants.length}명:\n`;
                errorMessage += `${missing_participants.join(', ')}\n`;
                errorMessage += `→ 일정 데이터가 없거나 배정 과정에서 누락되었습니다.`;
              }
              
              setError(errorMessage);
            } else {
              setError(null);
            }
          } else {
            setValidationData(null);
          }
        } else {
          setError('AI 일정 최적화에 실패했습니다.');
          setValidationData(null);
        }
      })
      .catch((error) => {
        setError(error.message || '일정 최적화 중 오류가 발생했습니다.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [updateTrigger]);
  
  // optimizedSchedule이 준비되면 부모에게 알림
  useEffect(() => {
    if (optimizedSchedule && onScheduleReady) {
      console.log('onScheduleReady 호출:', { optimizedSchedule: !!optimizedSchedule, validationData: !!validationData });
      onScheduleReady({
        optimizedSchedule,
        validationData
      });
    } else if (!optimizedSchedule && onScheduleReady) {
      // optimizedSchedule이 null이 되면 currentSchedule도 초기화
      console.log('optimizedSchedule이 null이므로 currentSchedule 초기화');
      onScheduleReady(null);
    }
  }, [optimizedSchedule, validationData, onScheduleReady]);
  
  // AI 최적화 결과 파싱 (동적 시간대) - useMemo로 메모이제이션
  const finalScheduleData = useMemo(() => {
    if (!optimizedSchedule) return {};
    
    const aiSchedule = {};
    const scheduleAssignments = optimizedSchedule.schedule_assignments || {};
    
    console.log('🔍 최적화 일정 파싱 시작:', { scheduleAssignments, participantsCount: participants.length, nameColumn });
    
    Object.keys(scheduleAssignments).forEach(dateKey => {
      const dayAssignments = scheduleAssignments[dateKey];
      if (!dayAssignments || typeof dayAssignments !== 'object') return;
      
      // weekday 정보 추출 및 저장
      const weekday = dayAssignments.weekday;
      
      aiSchedule[dateKey] = {
        date: dateKey,  // "07-13" 형식
        weekday: weekday,  // "일", "월", "화" 등
        sessions: {}
      };
      
      // 동적으로 모든 시간대 처리 (한 세션에 한 명만 배정)
      Object.keys(dayAssignments).forEach(timeSlot => {
        // weekday는 세션이 아니므로 건너뛰기
        if (timeSlot === 'weekday') return;
        
        const participantNames = Array.isArray(dayAssignments[timeSlot]) 
          ? dayAssignments[timeSlot] 
          : [dayAssignments[timeSlot]];
        
        // 첫 번째 참여자만 가져오기 (한 세션에 한 명)
        const firstParticipant = participantNames[0];
        if (firstParticipant && typeof firstParticipant === 'string') {
          const normalizedTargetName = String(firstParticipant).trim();
          const participantObj = participants.find(p => resolveParticipantName(p) === normalizedTargetName);
          
          if (participantObj) {
            aiSchedule[dateKey].sessions[timeSlot] = [{
              name: firstParticipant,
              participantIndex: participants.findIndex(p => resolveParticipantName(p) === normalizedTargetName),
              participant: participantObj
            }];
          } else {
            console.warn(`⚠️ 참여자 찾기 실패: "${firstParticipant}" (nameColumn: ${nameColumn})`);
            // 참여자 객체를 찾지 못해도 이름만이라도 표시
            aiSchedule[dateKey].sessions[timeSlot] = [{
              name: firstParticipant,
              participantIndex: -1,
              participant: null
            }];
          }
        }
      });
    });
    
    console.log('✅ 파싱 완료:', aiSchedule);
    return aiSchedule;
  }, [optimizedSchedule, participants, nameColumn, resolveParticipantName]); // optimizedSchedule, participants, nameColumn이 변경될 때만 재계산
  
  // LLM이 준 날짜 데이터만 사용하여 달력 생성
  const scheduleDateKeys = Object.keys(finalScheduleData);
  
  if (scheduleDateKeys.length === 0) {
    console.warn('⚠️ 최적화 일정 데이터가 없습니다.');
  }
  
  // 🔥 최우선: 로딩 중이면 로딩 UI만 표시 (달력이 나오기 전까지 계속 로딩)
  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', color: '#1976d2' }} />
        <p style={{ marginTop: '16px', color: '#666' }}>일정을 최적화하고 있습니다...</p>
      </div>
    );
  }

  // 🔥 두 번째: fatal error만 에러 UI 표시 (optimizedSchedule가 없을 때만)
  if (error && !optimizedSchedule) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ 
          background: '#ffebee', 
          border: '1px solid #f44336', 
          borderRadius: '8px',
          padding: '20px',
          color: '#c62828'
        }}>
          <p style={{ marginBottom: '8px', fontWeight: 600 }}>
            ⚠️ 일정 최적화 오류
          </p>
          <p style={{ fontSize: '14px', color: '#666' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              background: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            새로고침
          </button>
        </div>
      </div>
    );
  }

  // 🔥 세 번째: 로딩이 끝났는데 일정 데이터가 없으면 에러 메시지
  if (scheduleDateKeys.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ 
          background: '#fff3cd', 
          border: '1px solid #ffc107', 
          borderRadius: '8px',
          padding: '20px',
          color: '#856404'
        }}>
          <p style={{ marginBottom: '8px', fontWeight: 600 }}>
            ⚠️ 일정 데이터 없음
          </p>
          <p style={{ fontSize: '14px', color: '#666' }}>
            일정 최적화 데이터가 없습니다. 일정 최적화를 다시 시도해주세요.
          </p>
        </div>
      </div>
    );
  }
  
  // LLM이 준 날짜 데이터를 파싱하여 달력용 데이터 생성
  const weekdayMap = { '일': 0, '월': 1, '화': 2, '수': 3, '목': 4, '금': 5, '토': 6 };
  
  // 날짜를 MM-DD 형식으로 정렬
  const sortedDates = scheduleDateKeys
    .map(dateKey => {
      // MM-DD 형식 파싱
      const [month, day] = dateKey.split('-').map(Number);
      const dayData = finalScheduleData[dateKey];
      
      return {
        dateKey: dateKey,
        month: month,
        day: day,
        weekday: dayData?.weekday || '',
        weekdayIndex: dayData?.weekday ? weekdayMap[dayData.weekday] : null,
        sessions: dayData?.sessions || {}
      };
    })
    .sort((a, b) => {
      // 월 -> 일 순으로 정렬
      if (a.month !== b.month) return a.month - b.month;
      return a.day - b.day;
    });
  
  // 주 단위로 그룹화
  const weeks = [];
  let currentWeek = new Array(7).fill(null);
  
  sortedDates.forEach((dateInfo) => {
    const weekdayIndex = dateInfo.weekdayIndex;
    
    // weekdayIndex가 없거나 유효하지 않으면 건너뛰기
    if (weekdayIndex === null || weekdayIndex < 0 || weekdayIndex >= 7) {
      console.warn(`⚠️ 유효하지 않은 요일: ${dateInfo.weekday} (${dateInfo.dateKey})`);
      return;
    }
    
    // 현재 주에 해당 요일 위치에 날짜 배치
    if (currentWeek[weekdayIndex] === null) {
      currentWeek[weekdayIndex] = dateInfo;
    } else {
      // 같은 요일에 여러 날짜가 있으면 새로운 주 시작
      // 현재 주를 저장하고 새 주 시작
      weeks.push(currentWeek);
      currentWeek = new Array(7).fill(null);
      currentWeek[weekdayIndex] = dateInfo;
    }
  });
  
  // 마지막 주 추가
  if (currentWeek.some(day => day !== null)) {
    weeks.push(currentWeek);
  }
  
  
  // 요일 헤더
  const weekdayHeaders = ['일', '월', '화', '수', '목', '금', '토'];
  
  return (
    <div className="google-calendar-style">
      {/* 요일 헤더 */}
      <div className="calendar-weekday-header">
        {weekdayHeaders.map((day, idx) => (
          <div key={idx} className="calendar-weekday-cell">
            {day}
          </div>
        ))}
      </div>
      
      {/* 달력 그리드 */}
      {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="calendar-week-row">
          {week.map((dateInfo, dayIdx) => {
            // 빈 칸 처리
            if (!dateInfo) {
              return (
                <div 
                  key={dayIdx}
                  className="calendar-day-cell calendar-day-empty"
                />
              );
            }
            
            // dateInfo에서 정보 추출
            const { dateKey, day, weekday, sessions } = dateInfo;
            const hasSessions = sessions && Object.keys(sessions).length > 0;
            
            return (
              <div 
                key={dayIdx}
                className={`calendar-day-cell ${hasSessions ? 'calendar-day-has-events' : ''}`}
              >
                <div className="calendar-day-number">
                  {day}
                </div>
                <div className="calendar-day-weekday">
                  {weekday}
                </div>
                
                {hasSessions && (
                  <div className="calendar-day-events">
                    {Object.entries(sessions).map(([timeKey, participantArray], sessionIdx) => {
                      // participantArray가 배열인지 확인
                      const participantsList = Array.isArray(participantArray) ? participantArray : [participantArray];
                      
                      // 그룹별 색상 (참여자 그룹에 따라 색상 할당)
                      const groupColors = [
                        { bg: '#e8f0fe', border: '#1a73e8', text: '#1967d2' }, // 파란색
                        { bg: '#fce8e6', border: '#d93025', text: '#c5221f' }, // 빨간색
                        { bg: '#e6f4ea', border: '#137333', text: '#0d652d' }, // 초록색
                        { bg: '#fef7e0', border: '#f9ab00', text: '#ea8600' }, // 노란색
                        { bg: '#e8eaed', border: '#5f6368', text: '#3c4043' }, // 회색
                        { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' }, // 보라색
                      ];
                      
                      // 첫 번째 참여자의 그룹으로 색상 결정 (여러 참여자가 같은 세션에 있으면 첫 번째 참여자 기준)
                      let color = groupColors[sessionIdx % groupColors.length]; // 기본값
                      if (participants && participants.length > 0 && participantsList.length > 0) {
                        const firstParticipantName = participantsList[0]?.name || 
                          (participantsList[0]?.participant && participantsList[0].participant[nameColumn]) || 
                          (typeof participantsList[0] === 'string' ? participantsList[0] : '');
                        
                        if (firstParticipantName) {
                          // 참여자 목록에서 이름으로 찾기
                          const participantData = participants.find(p => {
                            const pName = (nameColumn && p[nameColumn]) ? String(p[nameColumn]) : '';
                            return pName === firstParticipantName;
                          });
                          
                          if (participantData && participantData._assigned_group) {
                            const assignedGroup = participantData._assigned_group;
                            const groupIndex = targetGroups?.findIndex(g => g.name === assignedGroup) ?? -1;
                            if (groupIndex >= 0) {
                              color = groupColors[groupIndex % groupColors.length];
                            }
                          }
                        }
                      }
                      
                      return (
                        <div 
                          key={timeKey} 
                          className="calendar-event-item"
                          style={{
                            borderLeft: `3px solid ${color.border}`,
                            background: color.bg,
                            color: color.text
                          }}
                        >
                          <div className="calendar-event-time">
                            {timeKey}
                          </div>
                          <div className="calendar-event-name">
                            {participantsList.map((p, idx) => {
                              if (!p) return null;
                              const name = p.name || (p.participant && p.participant[nameColumn]) || '이름 없음';
                              return (
                                <span key={idx}>
                                  {name}
                                  {idx < participantsList.length - 1 && ', '}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const PlanAnalysisViewer = ({ planJson, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedPlan, setEditedPlan] = useState(null);
  
  // 편집 모드 진입 시 현재 데이터 복사
  useEffect(() => {
    if (isEditing && !editedPlan) {
      setEditedPlan(JSON.parse(JSON.stringify(planJson)));
    }
  }, [isEditing, planJson, editedPlan]);
  
  if (!planJson) return null;
  
  const currentPlan = isEditing ? editedPlan : planJson;
  
  // 타겟 그룹 수정 함수
  const updateTargetGroup = (index, field, value) => {
    const updated = { ...editedPlan };
    updated.target_groups[index][field] = field === 'targetCount' ? parseInt(value) || 0 : value;
    setEditedPlan(updated);
  };
  
  const deleteTargetGroup = (index) => {
    const updated = { ...editedPlan };
    updated.target_groups = updated.target_groups.filter((_, i) => i !== index);
    setEditedPlan(updated);
  };
  
  const addTargetGroup = () => {
    const updated = { ...editedPlan };
    if (!updated.target_groups) updated.target_groups = [];
    updated.target_groups.push({ name: '새 그룹', targetCount: 0, description: '' });
    setEditedPlan(updated);
  };
  
  // 핵심 변수 수정 함수
  const updateKeyVariable = (index, field, value) => {
    const updated = { ...editedPlan };
    updated.key_variables[index][field] = value;
    setEditedPlan(updated);
  };
  
  const deleteKeyVariable = (index) => {
    const updated = { ...editedPlan };
    updated.key_variables = updated.key_variables.filter((_, i) => i !== index);
    setEditedPlan(updated);
  };
  
  const addKeyVariable = () => {
    const updated = { ...editedPlan };
    if (!updated.key_variables) updated.key_variables = [];
    updated.key_variables.push({ variable_name: '새 변수', description: '' });
    setEditedPlan(updated);
  };
  
  // 균형 변수 수정 함수
  const updateBalanceVariable = (index, field, value) => {
    const updated = { ...editedPlan };
    updated.balance_variables[index][field] = value;
    setEditedPlan(updated);
  };
  
  const deleteBalanceVariable = (index) => {
    const updated = { ...editedPlan };
    updated.balance_variables = updated.balance_variables.filter((_, i) => i !== index);
    setEditedPlan(updated);
  };
  
  const addBalanceVariable = () => {
    const updated = { ...editedPlan };
    if (!updated.balance_variables) updated.balance_variables = [];
    updated.balance_variables.push({ variable_name: '새 변수', description: '' });
    setEditedPlan(updated);
  };
  
  // 저장
  const handleSave = () => {
    onUpdate(editedPlan);
    setIsEditing(false);
    setEditedPlan(null);
  };
  
  // 취소
  const handleCancel = () => {
    setIsEditing(false);
    setEditedPlan(null);
  };
  
  return (
    <>
      {/* 타겟 그룹 */}
      {currentPlan?.target_groups && currentPlan.target_groups.length > 0 && (
        <div className="plan-section">
          <div className="plan-section-header">
            <div className="plan-title-with-add">
              <h3 className="plan-section-title">
                타겟 그룹
              </h3>
              {isEditing && (
                <button
                  onClick={addTargetGroup}
                  className="btn btn-secondary plan-add-btn"
                >
                  ➕ 그룹 구분 추가
                </button>
              )}
            </div>
            <div className="plan-header-actions">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn btn-primary plan-edit-btn"
                >
                  분석 결과 편집
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="btn btn-secondary plan-cancel-btn"
                  >
                    ✕ 취소
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-primary plan-save-btn"
                  >
                    ✓ 저장
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="plan-grid">
            {currentPlan.target_groups.map((item, index) => (
              <div key={index} className="plan-target-card">
                {isEditing ? (
                  <div className="plan-target-card-edit">
                    <div className="plan-target-card-edit-header">
                      <div className="plan-target-card-inputs">
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateTargetGroup(index, 'name', e.target.value)}
                          className="plan-target-input"
                          placeholder="그룹명"
                        />
                        <input
                          type="number"
                          value={item.targetCount}
                          onChange={(e) => updateTargetGroup(index, 'targetCount', e.target.value)}
                          className="plan-target-number-input"
                          placeholder="목표 인원"
                        />
                      </div>
                      <button
                        onClick={() => deleteTargetGroup(index)}
                        className="plan-delete-btn"
                      >
                        🗑️
                      </button>
                    </div>
                    <textarea
                      value={item.description}
                      onChange={(e) => updateTargetGroup(index, 'description', e.target.value)}
                      className="plan-target-textarea"
                      placeholder="그룹 특징 및 설명"
                    />
                  </div>
                ) : (
                  <>
                    <strong className="plan-target-name">
                      {item.name} ({item.targetCount}명)
                    </strong>
                    <p className="plan-target-description">
                      {item.description}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 핵심 선별 변수 */}
      {currentPlan?.key_variables && currentPlan.key_variables.length > 0 && (
        <div className="plan-section">
          <div className="plan-section-header">
            <div className="plan-title-with-add">
              <h3 className="plan-section-title">
                핵심 선별 변수
              </h3>
              {isEditing && (
                <button
                  onClick={addKeyVariable}
                  className="btn btn-secondary plan-add-btn"
                >
                  ➕ 핵심 변수 추가
                </button>
              )}
            </div>
          </div>
          <div className="plan-grid">
            {currentPlan.key_variables.map((item, index) => (
              <div key={index} className="plan-key-card">
                {isEditing ? (
                  <div className="plan-target-card-edit">
                    <div className="plan-target-card-edit-header">
                      <input
                        type="text"
                        value={item.variable_name}
                        onChange={(e) => updateKeyVariable(index, 'variable_name', e.target.value)}
                        className="plan-key-input"
                        placeholder="변수명"
                      />
                      <button
                        onClick={() => deleteKeyVariable(index)}
                        className="plan-delete-btn"
                      >
                        🗑️
                      </button>
                    </div>
                    <textarea
                      value={item.description}
                      onChange={(e) => updateKeyVariable(index, 'description', e.target.value)}
                      className="plan-key-textarea"
                      placeholder="변수 설명 및 조건"
                    />
                  </div>
                ) : (
                  <>
                    <strong className="plan-key-name">
                      {item.variable_name}
                    </strong>
                    <p className="plan-key-description">
                      {item.description}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 균형 배분 변수 */}
      {currentPlan?.balance_variables && currentPlan.balance_variables.length > 0 && (
        <div className="plan-section">
          <div className="plan-section-header">
            <div className="plan-title-with-add">
              <h3 className="plan-section-title">
                균형 배분 변수
              </h3>
              {isEditing && (
                <button
                  onClick={addBalanceVariable}
                  className="btn btn-secondary plan-add-btn"
                >
                  ➕ 균형 변수 추가
                </button>
              )}
            </div>
          </div>
          <div className="plan-grid">
            {currentPlan.balance_variables.map((item, index) => (
              <div key={index} className="plan-balance-card">
                {isEditing ? (
                  <div className="plan-target-card-edit">
                    <div className="plan-target-card-edit-header">
                      <input
                        type="text"
                        value={item.variable_name}
                        onChange={(e) => updateBalanceVariable(index, 'variable_name', e.target.value)}
                        className="plan-balance-input"
                        placeholder="변수명"
                      />
                      <button
                        onClick={() => deleteBalanceVariable(index)}
                        className="plan-delete-btn"
                      >
                        🗑️
                      </button>
                    </div>
                    <textarea
                      value={item.description}
                      onChange={(e) => updateBalanceVariable(index, 'description', e.target.value)}
                      className="plan-balance-textarea"
                      placeholder="균형 배분 기준 설명"
                    />
                  </div>
                ) : (
                  <>
                    <strong className="plan-balance-name">
                      {item.variable_name}
                    </strong>
                    <p className="plan-balance-description">
                      {item.description}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};


const MappingResultViewer = ({ mappingResult, csvInfo }) => {
  if (!mappingResult) return null;
  const keyMappings = mappingResult.key_variable_mappings || mappingResult.variable_mappings || [];
  const balanceMappings = mappingResult.balance_variable_mappings || [];
  
  // CSV 스키마를 참고하여 컬럼명을 실제 내용으로 변환
  const getColumnDescription = (columnName) => {
    if (!csvInfo?.schema || !columnName) return columnName;
    
    const columnSchema = csvInfo.schema.find(col => col.column_name === columnName);
    if (!columnSchema) return columnName;
    
    // values_sample에서 실제 질문 내용 추출
    const valuesSample = columnSchema.values_sample || [];
    if (valuesSample.length > 0) {
      // 첫 번째 샘플이 질문일 가능성이 높음 (너무 길면 줄임)
      const firstSample = String(valuesSample[0]);
      if (firstSample.length < 100 && !firstSample.includes('아래 조사')) {
        return `${columnName} - ${firstSample}`;
      }
    }
    
    return columnName;
  };
  
  const getConfidenceBadge = (confidence = 'Low') => {
    const confLower = String(confidence).toLowerCase();
    let className = 'badge-';
    let icon = faCheckCircle;
    if (confLower === 'high') className += 'high';
    else if (confLower === 'medium') { className += 'medium'; icon = faExclamationTriangle; }
    else { className += 'low'; icon = faQuestionCircle; }
    return <span className={`confidence-badge ${className}`}><FontAwesomeIcon icon={icon} /> {confidence}</span>;
  };
  
  const tableStyle = { width: '100%', tableLayout: 'fixed' };
  const ellipsisCell = (content) => (
    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{content}</div>
  );

  const renderPlanCell = (title, description) => (
    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      <strong>{title}</strong>
      {description && (
        <>
          <br />
          <small style={{ color: '#666' }}>{description}</small>
        </>
      )}
    </div>
  );

  const renderColumnCell = (columnName) => {
    if (!columnName) {
      return <span className="text-muted">매핑 실패</span>;
    }

    const description = getColumnDescription(columnName);
    return (
      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <code style={{ fontSize: '13px' }}>{columnName}</code>
        {description && description !== columnName && (
          <>
            <br />
            <small style={{ color: '#666' }}>{description}</small>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="participants-result-box">
      <h4><FontAwesomeIcon icon={faCheckCircle} /> 변수 맵핑 완료</h4>

      {keyMappings.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h5 style={{ marginBottom: '12px' }}>핵심 변수 맵핑</h5>
          <table className="participants-table" style={tableStyle}>
            <colgroup>
              <col style={{ width: '36%' }} />
              <col style={{ width: '44%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>핵심 변수 (From Plan)</th>
                <th>매핑된 컬럼 (From CSV)</th>
                <th>정확도 (AI)</th>
              </tr>
            </thead>
            <tbody>
              {keyMappings.map((item, index) => (
                <tr key={`key-${index}`}>
                  <td>{renderPlanCell(item.key_variable, item.description)}</td>
                  <td>{renderColumnCell(item.mapped_column)}</td>
                  <td>{ellipsisCell(getConfidenceBadge(item.confidence))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {balanceMappings.length > 0 && (
        <div>
          <h5 style={{ marginBottom: '12px' }}>균형 변수 맵핑</h5>
          <table className="participants-table" style={tableStyle}>
            <colgroup>
              <col style={{ width: '36%' }} />
              <col style={{ width: '44%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>균형 변수 (From Plan)</th>
                <th>매핑된 컬럼 (From CSV)</th>
                <th>정확도 (AI)</th>
              </tr>
            </thead>
            <tbody>
              {balanceMappings.map((item, index) => (
                <tr key={`balance-${index}`}>
                  <td>{renderPlanCell(item.balance_variable, item.description)}</td>
                  <td>{renderColumnCell(item.mapped_column)}</td>
                  <td>{ellipsisCell(getConfidenceBadge(item.confidence))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CriteriaResultViewer = ({ criteriaResult }) => {
  if (!criteriaResult) return null;
  const criteria = criteriaResult.scoring_criteria || [];
  
  // 추가 안전 체크
  if (!Array.isArray(criteria)) {
    return <div>기준 데이터가 올바르지 않습니다.</div>;
  }

  return (
    <div className="screener-result-box">
      <h4><FontAwesomeIcon icon={faCheckCircle} /> 그룹별 답변 기준 생성 완료</h4>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: '20px',
        marginTop: '16px'
      }}>
        {criteria.map((group, index) => (
          <div key={index} style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            background: '#fff'
          }}>
            <h5 style={{ marginTop: 0, marginBottom: '12px', color: '#333' }}>
              🎯 {group.group_name}
            </h5>
            {group.logic?.map((variable, vIndex) => (
              <div key={vIndex} style={{ marginBottom: '16px' }}>
                <strong style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
                  {variable.description || variable.column_name}
                </strong>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0 
                }}>
                  {variable.rules?.map((rule, ruleIndex) => (
                    <li key={ruleIndex} className="rule-line" style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px',
                      padding: '8px',
                      background: '#f8f9fa',
                      borderRadius: '4px'
                    }}>
                      {(() => {
                        // 한국어 조사 처리 함수
                        const getKoreanParticle = (text, withParticle, withoutParticle) => {
                          if (!text || typeof text !== 'string') return withParticle;
                          const trimmed = text.trim();
                          
                          // 마지막 한글 문자 찾기 (역순으로 검색)
                          let lastKoreanChar = null;
                          for (let i = trimmed.length - 1; i >= 0; i--) {
                            const char = trimmed[i];
                            const charCode = char.charCodeAt(0);
                            
                            // 한글인지 확인 (가-힣)
                            if (charCode >= 0xAC00 && charCode <= 0xD7A3) {
                              lastKoreanChar = char;
                              break;
                            }
                          }
                          
                          // 한글 문자가 없으면 기본값
                          if (!lastKoreanChar) {
                            return withParticle;
                          }
                          
                          // 받침 유무 확인: (문자코드 - 0xAC00) % 28
                          const lastCharCode = lastKoreanChar.charCodeAt(0);
                          const hasBatchim = (lastCharCode - 0xAC00) % 28 !== 0;
                          return hasBatchim ? withParticle : withoutParticle;
                        };
                        
                        let conditionText = '';
                        if (variable.type === 'numerical') {
                          conditionText = `값이 ${rule.range[0]} ~ ${rule.range[1]} 사이인 경우`;
                        } else if (variable.type === 'categorical') {
                          const matchMode = rule.match_mode || rule.match_type || 'exact';
                          const value = rule.value || '';
                          
                          if (matchMode === 'contains') {
                            // 포함 모드: "값에 네이버 지도/기차가 포함된 경우"
                            const particle = getKoreanParticle(value, '이', '가');
                            conditionText = (
                              <>
                                값에 <code>{value}</code>{particle} 포함된 경우
                              </>
                            );
                          } else {
                            // 일치 모드: "값이 네이버 지도/기차와 일치하는 경우"
                            const particle = getKoreanParticle(value, '과', '와');
                            conditionText = (
                              <>
                                값이 <code>{value}</code>{particle} 일치하는 경우
                              </>
                            );
                          }
                        } else if (variable.type === 'contains') {
                          conditionText = <>텍스트에 <code>{rule.keyword}</code>가 포함될 경우</>;
                        } else {
                          conditionText = '정의되지 않은 규칙';
                        }
                        
                        return (
                          <>
                            <span>{conditionText}</span>
                            <strong style={{ color: rule.points > 0 ? '#198754' : '#dc3545' }}>
                              {rule.points > 0 ? `+${rule.points}` : rule.points}점
                            </strong>
                          </>
                        );
                      })()}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const FinalSelectionViewer = ({ finalSelection }) => {
  if (!finalSelection) return null;
  
  // JSON 객체인 경우 텍스트로 변환
  const displayText = typeof finalSelection === 'object' 
    ? JSON.stringify(finalSelection, null, 2)
    : String(finalSelection);
  
  return (
    <div className="screener-result-box">
      <h4><FontAwesomeIcon icon={faCheckCircle} /> 최종 선별 결과</h4>
      <div className="final-selection-content">
        <pre style={{whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: '1.6'}}>
          {displayText}
        </pre>
      </div>
    </div>
  );
};

const ParticipantsPicker = () => {
  const { studySlug } = useParams(); // slug로 변경
  const navigate = useNavigate();
  
  const [studyId, setStudyId] = useState(null); // slug로 조회 후 ID 저장
  
  const {
    finalPlanText,
    screenerState,
    screenerLoading,
    step1Loading,
    step2Loading,
    step3Loading,
    loadStep1FromSurvey,
    loadScreenerDataFromDB,
    loadStudyPlanFromDB,
    handleProcessCsvComplete,
    handleAutoScoringComplete,
    getStudy,
    currentStudy,
    handleScreenerPlanAnalysisV2,
    handleScreenerUploadV2,
    handleFindOptimalParticipants,
    resetCsvAndStepResults,
    updatePlanJson,
  } = useAppContext();
  const analysisTriggeredRef = useRef(false);
  const [openPanels, setOpenPanels] = useState({ 
    step1: true, 
    step2: true, 
    step3: false, 
    step4: false, 
    step5: false 
  });

  // 파일 업로드 상태 관리
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // 'idle', 'analyzing', 'completed'
  const [analysisResult, setAnalysisResult] = useState(null);
  const [step1Collapsed, setStep1Collapsed] = useState(true);
  const [uploadedFileContent, setUploadedFileContent] = useState('');
  
  // 현재 csvInfo에 연결된 파일명 추적 (csvInfo가 다른 파일의 것인지 확인)
  const [csvInfoFileName, setCsvInfoFileName] = useState(null);

  // Step 4 상태 관리
  const [allParticipantsData, setAllParticipantsData] = useState([]); // 전체 participants_data 저장
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]); // 사용자가 체크한 참여자 ID 목록
  const [finalizedParticipants, setFinalizedParticipants] = useState([]); // 최종 선정된 참여자 목록 (Step5 표시용)
  const [finalizationResult, setFinalizationResult] = useState(null); // 그룹/요약 정보
  const step3Ref = useRef(null);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [showScheduleWarning, setShowScheduleWarning] = useState(false);
  const [finalizeError, setFinalizeError] = useState(false);
  const [scheduleUpdateTrigger, setScheduleUpdateTrigger] = useState(0); // 달력 업데이트 트리거
  const [currentSchedule, setCurrentSchedule] = useState(null); // 현재 달력에 표시된 일정 데이터
  const [isSaving, setIsSaving] = useState(false); // 저장 중 상태
  const [toast, setToast] = useState({ isOpen: false, type: 'success', message: '' }); // 토스트 상태

  const hasNameColumn = Boolean(screenerState.csvInfo?.has_name_column);

  // slug로 study 조회
  useEffect(() => {
    if (!studySlug) {
      console.error("studySlug가 없습니다.");
      setStudyId(null);
      return;
    }

    const loadStudyBySlug = async () => {
      try {
        console.log("Study 조회 시작:", studySlug);
        const studyRes = await apiFetch(`/studies/by-slug/${studySlug}`, { method: 'GET' });
        if (studyRes.ok) {
          const studyData = await studyRes.json();
          console.log("Study 조회 성공:", studyData);
          if (studyData?.id) {
            setStudyId(studyData.id); // ID 저장
          } else {
            console.error("Study 데이터에 id가 없습니다:", studyData);
            setStudyId(null);
          }
        } else {
          const errorText = await studyRes.text();
          console.error("Study 조회 실패:", studyRes.status, errorText);
          setStudyId(null);
        }
      } catch (err) {
        console.error("Study 로드 오류:", err);
        setStudyId(null);
      }
    };

    loadStudyBySlug();
  }, [studySlug]);

  // Step 4 결과가 갱신되면 로컬 상태 동기화
  useEffect(() => {
    if (screenerState.participantsData && screenerState.participantsData.length > 0) {
      setAllParticipantsData(screenerState.participantsData);

      // 초기 선택값이 없는 경우, 기존 플래그 기반으로 자동 선택
      if (selectedParticipantIds.length === 0) {
        const preselected = screenerState.participantsData
          .filter(p => p._is_selected || p._is_user_selected)
          .map(p => p.participant_id || p.id)
          .filter(Boolean)
          .map(String);
        if (preselected.length > 0) {
          setSelectedParticipantIds(Array.from(new Set(preselected)));
        }
      }
    }
  }, [screenerState.participantsData, selectedParticipantIds.length]);
  
  // Step5 최종 선정 핸들러
  const handleSmartSelectionAndSchedule = async () => {
    if (finalizeLoading) return;

    try {
      const baseParticipants = screenerState.participantsData?.length > 0
        ? screenerState.participantsData
        : allParticipantsData;

      if (!baseParticipants || baseParticipants.length === 0) {
        alert('4단계 결과를 찾을 수 없습니다. 먼저 4단계를 완료해주세요.');
        return;
      }

      setFinalizeLoading(true);
      setFinalizeError(false);

      const planJson = screenerState.planJson;
      const targetGroups = planJson?.target_groups || [];
      const totalTarget = targetGroups.reduce((sum, g) => sum + (g.targetCount || 0), 0);

      const uniqueSelectedIds = Array.from(new Set(selectedParticipantIds.map(String)));

      const result = await finalizeParticipants(
        studyId,
        baseParticipants,
        uniqueSelectedIds,
        targetGroups,
        screenerState.csvInfo,
        planJson
      );

      if (result.success) {
        const finalList = result.final_participants || [];
        const nameColumnKey = screenerState.csvInfo?.name_column;
        const scheduleColumnKeys = screenerState.csvInfo?.schedule_columns || [];
        const hasNameColumnFlag = hasNameColumn;

        const getParticipantKey = (participant) => {
          if (!participant || typeof participant !== 'object') return '';
          return String(
            participant.participant_id ||
            participant.id ||
            participant.participantId ||
            ''
          );
        };

        const baseParticipantsMap = new Map();
        baseParticipants.forEach(p => {
          const key = getParticipantKey(p);
          if (key) {
            baseParticipantsMap.set(key, p);
          }
        });

        const mergeWithBase = (participant) => {
          const key = getParticipantKey(participant);
          if (!key) return participant;

          const baseData = baseParticipantsMap.get(key);
          if (!baseData) return participant;

          const merged = { ...baseData };

          if (participant && typeof participant === 'object') {
            Object.entries(participant).forEach(([field, value]) => {
              if (value === undefined || value === null) {
                return;
              }

              if (typeof value === 'string' && value.trim() === '') {
                // 메타 필드나 status 류는 그대로 반영
                if (!field.startsWith('_') && field !== 'participant_id' && field !== 'id') {
                  return;
                }
              }

              merged[field] = value;
            });
          }

          return merged;
        };

        const enrichParticipant = (participant) => {
          if (!participant || typeof participant !== 'object') return participant;

          const enriched = { ...participant };

          if (!hasNameColumnFlag && nameColumnKey && !enriched[nameColumnKey] && enriched._display_name) {
            enriched[nameColumnKey] = enriched._display_name;
          }

          if (Array.isArray(scheduleColumnKeys) && scheduleColumnKeys.length > 0) {
            const scheduleValues = enriched._schedule_values;
            if (scheduleValues && typeof scheduleValues === 'object') {
              scheduleColumnKeys.forEach(col => {
                if (!enriched[col] && scheduleValues[col]) {
                  enriched[col] = scheduleValues[col];
                }
              });
            }
          }

          return enriched;
        };

        const mergedFinalList = finalList.map(participant => enrichParticipant(mergeWithBase(participant)));
        const mergedReserveList = (result.reserve_participants || []).map(participant => enrichParticipant(mergeWithBase(participant)));
        const mergedGroups = (result.groups || []).map(group => ({
          ...group,
          final_participants: (group.final_participants || []).map(participant => enrichParticipant(mergeWithBase(participant))),
          reserve_participants: (group.reserve_participants || []).map(participant => enrichParticipant(mergeWithBase(participant)))
        }));

        setFinalizedParticipants(mergedFinalList);
        setFinalizationResult({
          ...result,
          final_participants: mergedFinalList,
          reserve_participants: mergedReserveList,
          groups: mergedGroups
        });

        // Step 5 패널 열기
        setOpenPanels(prev => ({
          ...prev,
          step3: false,
          step4: false,
          step5: true
        }));

        // 달력 업데이트 (일정 컬럼이 있는 경우에만)
        if ((result.schedule_columns?.length || 0) > 0 && mergedFinalList.length > 0) {
          setScheduleUpdateTrigger(prev => prev + 1);
        }

        setTimeout(() => {
          const step5Element = document.getElementById('participants-step5');
          if (step5Element) {
            step5Element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);

        const userCount = result.summary?.total_user_selected ?? uniqueSelectedIds.length;
        const finalCount = result.summary?.total_final_participants ?? mergedFinalList.length;
        const autoCount = Math.max(finalCount - userCount, 0);

        if (userCount > 0) {
          alert(`최종 선정 완료! 총 ${totalTarget}명 중 ${userCount}명 선택 + ${autoCount}명 자동 추천 = ${finalCount}명 구성`);
        } else {
          alert(`최종 선정 완료! 총 ${finalCount}명 자동 추천 완료`);
        }
      } else {
        setFinalizeError(true);
        alert('최종 선정 실패: ' + (result.error || '알 수 없는 오류'));
      }
    } catch (error) {
      console.error('최종 선정 오류:', error);
      setFinalizeError(true);
      alert('최종 선정 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setFinalizeLoading(false);
    }
  };
  
  // 디버깅 추가
  const togglePanel = (panelKey) => {
    setOpenPanels(prev => ({
      ...prev,
      [panelKey]: !prev[panelKey]
    }));
  };
  
  // 일정 저장 핸들러
  const handleSaveSchedule = async () => {
    console.log('handleSaveSchedule 호출:', { 
      currentSchedule: !!currentSchedule, 
      studyId, 
      studySlug,
      finalizedParticipants: finalizedParticipants?.length 
    });
    
    if (!studySlug) {
      alert('연구 정보를 찾을 수 없습니다. 페이지를 새로고침해주세요.');
      return;
    }
    
    if (!studyId) {
      alert('연구 ID를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    
    if (!finalizedParticipants || finalizedParticipants.length === 0) {
      alert('최종 선정된 참여자가 없습니다. 먼저 최종 선정을 완료해주세요.');
      return;
    }
    
    if (!currentSchedule || !currentSchedule.optimizedSchedule) {
      alert('저장할 일정이 없습니다. 일정 최적화가 완료되지 않았습니다.');
      return;
    }
    
    setIsSaving(true);
    try {
      const result = await saveSchedule(
        studyId,
        currentSchedule.optimizedSchedule,
        finalizedParticipants,
        screenerState.csvInfo?.schedule_columns || [],
        screenerState.csvInfo?.name_column || '이름',
        screenerState.planJson?.target_groups || [],
        currentSchedule.validationData || {}
      );
      
      if (result.success) {
        const totalCount = result.saved_participants_count || finalizedParticipants.length;
        const assignedCount = result.assigned_count || 0;
        const unassignedCount = result.unassigned_count || (totalCount - assignedCount);
        
        let message = `일정이 저장되었습니다. 총 ${totalCount}명 중 ${assignedCount}명 일정 배정 완료`;
        if (unassignedCount > 0) {
          message += ` (${unassignedCount}명 일정 미배정)`;
        }
        
        setToast({
          isOpen: true,
          type: 'success',
          message: message
        });
        
        // 커스텀 이벤트를 발생시켜 StudyWorkflowPage가 데이터를 다시 로드하도록 함
        // 이렇게 하면 뒤로 가기로 돌아와도 일정 확인 버튼이 활성화됨
        window.dispatchEvent(new CustomEvent('scheduleSaved', { detail: { studyId } }));
      } else {
        setToast({
          isOpen: true,
          type: 'error',
          message: '저장 실패: ' + (result.error || '알 수 없는 오류')
        });
      }
    } catch (error) {
      console.error('일정 저장 오류:', error);
      setToast({
        isOpen: true,
        type: 'error',
        message: '저장 중 오류가 발생했습니다.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 2. CSV 업로드 핸들러 수정 (258번 라인)
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // 파일 형식 검증
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('CSV 파일만 업로드 가능합니다.');
      return;
    }
    
    // 파일 크기 검증 (50MB 제한)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('파일 크기는 50MB를 초과할 수 없습니다.');
      return;
    }
    
    // ⚠️ 파일 변경 시 이전 실행 취소 및 UI 즉시 초기화
    console.log('📁 새 파일 업로드 시작 - 이전 결과 초기화');
    resetCsvAndStepResults();
    
    // ⚠️ 파일명을 업로드 시작 전에 바로 세팅 (csvInfo 도착 즉시 표시하기 위해)
    setCsvInfoFileName(file.name);
    
    try {
      setUploadedFile(file);
      setAnalysisStatus('analyzing');
      setAnalysisResult(null);
      
      const content = await file.text();
      setUploadedFileContent(content);
      
      // handleScreenerUploadV2 내부에서도 실행 ID가 증가됨 (이중 보호)
      await handleScreenerUploadV2(content);
      
      setAnalysisStatus('completed');
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      alert(`파일 업로드 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
      
      // 에러 발생 시 상태 초기화
      setUploadedFile(null);
      setAnalysisStatus('idle');
      setAnalysisResult(null);
      setCsvInfoFileName(null);
      setUploadedFileContent('');
    }
  };
  // 분석 상태를 추적하기 위한 ref (declared once above)
  
  // 현재 연구 정보
  const study = getStudy(studyId);
  const [studyName, setStudyName] = useState('');

  usePageHeader(
    {
      title: '참여자 선별기',
      subtitle: studyName || '',
      showBack: true,
      onBack: () => navigate(-1),
    },
    [studyName]
  );

  useEffect(() => {
    // 1) 컨텍스트에서 바로 획득
    const nameFromContext = study?.name || currentStudy?.name;
    if (nameFromContext) {
      setStudyName(nameFromContext);
      return;
    }
    // 2) 서버에서 연구 정보 직접 조회 → 3) 아티팩트의 study_name으로 최종 보완
    let canceled = false;
    (async () => {
      try {
        const studyRes = await apiFetch(`/studies/${studyId}`, { method: 'GET' });
        if (!canceled && studyRes.ok) {
          const studyData = await studyRes.json();
          if (studyData?.name) {
            setStudyName(studyData.name);
            return;
          }
        }
      } catch (e) {
        // ignore
      }
      try {
        const res = await getStudyArtifacts(studyId);
        if (!canceled && res?.success && Array.isArray(res.artifacts)) {
          const found = res.artifacts.find(a => a.study_name)?.study_name || '';
          if (found) setStudyName(found);
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { canceled = true; };
  }, [studyId, study, currentStudy]);
  // NOTE: analysisTriggeredRef already declared above; remove duplicate
  
  // 스터디별 스크리너 데이터를 데이터베이스에서 불러오기
  useEffect(() => {
    let isMounted = true; // 컴포넌트가 마운트된 상태인지 확인
    
    const loadScreenerData = async () => {
      if (!studyId || !isMounted) return;
      
      try {
        // 1. 데이터베이스에서 계획서를 직접 로드
        const planText = await loadStudyPlanFromDB(studyId);
        
        if (!isMounted) return;
        
        // 2. 데이터베이스에서 모든 스크리너 데이터를 불러오기 시도
        const screenerData = await loadScreenerDataFromDB(studyId);
        
        if (!isMounted) return;
        
        if (screenerData) {
          // Step 1 데이터가 없고 계획서가 있고, 아직 분석을 트리거하지 않았다면 새로 분석 실행
          if (!screenerData.step1?.data && planText && !analysisTriggeredRef.current) {
            analysisTriggeredRef.current = true;
            handleScreenerPlanAnalysisV2(planText, studyId); // V2 함수 사용
          }
        } else {
          // 데이터베이스에서 데이터를 찾을 수 없으면 Step 1만 시도
          const loaded = await loadStep1FromSurvey(studyId);
          
          if (!isMounted) return;
          
          if (!loaded && planText && !analysisTriggeredRef.current) {
            analysisTriggeredRef.current = true;
            handleScreenerPlanAnalysisV2(planText, studyId); // V2 함수 사용
          }
        }
      } catch (error) {
        console.error('[ERROR] 스크리너 데이터 로드 실패:', error);
      }
    };
    
    loadScreenerData();
    
    // 클린업 함수
    return () => {
      isMounted = false;
    };
  }, [studyId]); // studyId만 의존성으로 설정하여 무한 루프 방지

  // step3 완료되면 step4를 자동으로 열고, finalSelection이 있으면 스크롤
  useEffect(() => {
    if (!step3Loading) {
      // step 3가 완료되면 무조건 step4를 엶
      setOpenPanels(prev => ({ ...prev, step4: true }));
      
      // finalSelection이 있으면 스크롤
      if (screenerState.finalSelection) {
        setTimeout(() => {
          const step4Element = document.getElementById('participants-step4');
          if (step4Element) {
            step4Element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, [step3Loading, screenerState.finalSelection]);

  return (
    <WorkspaceLayout>
      <div className="participants-page">
        {/* 단계별 컨테이너 */}
        <div className="participants-steps">
          
          {/* Step 1: 계획서 분석 */}
          <div className="participants-step-container">
            <div className="participants-step-card">
              <div
                className={`participants-step-header ${openPanels.step1 ? 'open' : ''}`}
                onClick={() => togglePanel('step1')}
              >
                <h2 className="participants-step-title">
                  1단계. 변수 분석 결과
                </h2>
                <Icon24 
                  name={openPanels.step1 ? "folding" : "floating"}
                  className={`participants-step-chevron ${openPanels.step1 ? 'open' : ''}`}
                />
              </div>

              {openPanels.step1 && (
                <div className="participants-step-content">
                  {step1Loading ? (
                    <div className="participants-loading-container">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        size="2x"
                        className="participants-loading-icon"
                      />
                      <p className="participants-loading-text">
                        계획서를 분석하고 있습니다...
                      </p>
                    </div>
                  ) : screenerState.planJson ? (
                    <div className="participants-step1-container">
                      <div className={`participants-step1-content ${step1Collapsed ? 'participants-step1-collapsed' : ''}`}>
                        <PlanAnalysisViewer 
                          planJson={screenerState.planJson} 
                          onUpdate={updatePlanJson}
                        />
                      </div>
                      {step1Collapsed && (
                        <div className="participants-step1-gradient" />
                      )}
                      <div className="participants-step1-toggle">
                        <button
                          onClick={() => setStep1Collapsed(prev => !prev)}
                          className="btn btn-secondary participants-step1-toggle-btn"
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <Icon24 name={step1Collapsed ? "floating" : "folding"} />
                          {step1Collapsed ? '자세히 보기' : '접기'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="participants-waiting-container">
                      <p className="participants-waiting-text">
                        ⏳ 계획서 분석 대기 중...
                      </p>
                      <p className="participants-waiting-subtext">
                        기존 스크리너 설문의 Step1 결과를 불러오거나 새로 분석을 실행합니다.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: CSV 업로드 및 분석 */}
            <div className="participants-step-card">
              <div
                className={`participants-step-header ${openPanels.step2 ? 'open' : ''}`}
                onClick={() => togglePanel('step2')}
              >
                <h2 className="participants-step-title">
                  2단계. 설문응답 데이터 업로드 및 분석
                </h2>
                <Icon24 
                  name={openPanels.step2 ? "folding" : "floating"}
                  className={`participants-step-chevron ${openPanels.step2 ? 'open' : ''}`}
                />
              </div>

              {openPanels.step2 && (
                <div className="participants-step-content">
                  <div className="participants-upload-section">
                    {/* 파일 업로드 input (항상 렌더링하되 숨김) */}
                    <input 
                      type="file" 
                      accept=".csv" 
                      className="participants-file-input"
                      id="csv-upload"
                      disabled={step2Loading || step3Loading} 
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      style={{ display: 'none' }}
                    />
                    
                    {/* 파일 업로드 영역 */}
                    {!uploadedFile && (
                      <div className="participants-file-upload-wrapper">
                        {/* 가이드 정보 */}
                        <div className="participants-guide-container">
                          <div className="participants-guide-header">
                            ✅ CSV 파일 준비 안내
                          </div>
                          <ul className="participants-guide-list">
                            <li><strong>1행은 질문(헤더)</strong>으로, 2행부터 응답자의 답변 데이터를 배치해 주세요.</li>
                            <li>다중 응답은 하나의 셀 안에 쉼표(,)로 구분하여 입력해야 해요.</li>
                            <li><strong>[선택 사항]</strong> 일정 생성을 위해 <strong>참여 가능일</strong>에 대한 응답을 포함해 주세요.</li>
                            <li>
                              <strong>[선택 사항]</strong> 이름/연락처 항목을 포함하면 선별 및 일정 관리가 편리해져요.
                              <span className="optional-note">
                                &nbsp;* 없을 경우 행 번호로 구분합니다.
                              </span>
                            </li>
                          </ul>
                          <div className="participants-privacy-notice">
                            <span>🔒</span>
                            <span>참여자 개인정보는 일절 저장하지 않으며, 일정 저장 시에는 모든 정보가 마스킹 처리되어 안전하게 보관됩니다.</span>
                          </div>
                        </div>
                        
                        {/* 파일 선택 영역 */}
                        <label 
                          htmlFor="csv-upload" 
                          className={`participants-upload-area ${(step2Loading || step3Loading) ? 'disabled' : ''}`}
                          style={{ pointerEvents: (step2Loading || step3Loading) ? 'none' : 'auto' }}
                        >
                          <FontAwesomeIcon icon={faUpload} className="participants-upload-icon" />
                          <div className="participants-upload-title">
                            CSV 파일을 선택하세요
                          </div>
                        </label>
                      </div>
                    )}

                    {/* 업로드된 파일 정보 및 분석 상태 */}
                    {uploadedFile && (
                      <div className="participants-file-info">
                        <div className="participants-file-display">
                          <div className="participants-file-left">
                            <Icon36 name="file" className="participants-file-icon" />
                            <div className="participants-file-details">
                              <div className="participants-file-name">
                                {uploadedFile.name}
                              </div>
                            </div>
                            <button 
                              className="participants-file-change-btn"
                              disabled={step2Loading}
                              onClick={async () => {
                                // 업로드 중이면 무시
                                if (step2Loading) return;
                                
                                // 실행 중인 작업이 있다면 먼저 확인
                                if (step3Loading) {
                                  const confirm = window.confirm(
                                    '아직 진행 중인 참여자 찾기가 있습니다. 취소하고 새 파일을 업로드하시겠습니까?'
                                  );
                                  if (!confirm) return;
                                }
                                
                                // AppContext의 초기화 함수 호출 (로딩 상태도 초기화됨)
                                resetCsvAndStepResults();
                                
                                // 로컬 상태 초기화
                                setCsvInfoFileName(null);
                                setUploadedFile(null);
                                setAnalysisStatus('idle');
                                setAnalysisResult(null);
                                setUploadedFileContent('');
                                
                                // 파일 input 초기화 (같은 파일 재선택 가능하도록)
                                const fileInput = document.getElementById('csv-upload');
                                if (fileInput) {
                                  fileInput.value = '';
                                }
                              }}
                            >
                              {step2Loading ? '업로드 중...' : '파일 변경'}
                            </button>
                          </div>
                          {screenerState.csvInfo && uploadedFile && csvInfoFileName === uploadedFile.name && !step2Loading && (
                            <div className="participants-file-right">
                              <span className="participants-file-badge participants-file-badge-rows">
                                총 {screenerState.csvInfo.total_rows ?? '-'} 행
                              </span>
                              <span className="participants-file-badge participants-file-badge-columns">
                                총 {screenerState.csvInfo.schema?.length ?? '-'} 열
                              </span>
                              <span className="participants-file-badge participants-file-badge-open">
                                이름/연락처: {screenerState.csvInfo.name_column ? '있음' : '없음'}
                              </span>
                              <span className="participants-file-badge participants-file-badge-open">
                                선호 일정: {screenerState.csvInfo.schedule_columns?.length > 0 ? '있음' : '없음'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* 로딩과 메시지 표시 */}
                    {step2Loading && (
                      <div className="participants-upload-loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        CSV 업로드 및 프로파일링 중입니다. 시간이 소요될 수 있습니다.
                      </div>
                    )}
                    
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: 변수 맵핑 및 기준 생성 결과 - 동적 생성 */}
            {(screenerState.mappingResult || step3Loading) && (
              <div className="participants-step-card" ref={step3Ref}>
                <div
                  className={`participants-step-header ${openPanels.step3 ? 'open' : ''}`}
                  onClick={() => togglePanel('step3')}
                >
                  <h2 className="participants-step-title">
                    3단계. 변수 맵핑 및 기준 생성
                  </h2>
                  <Icon24 
                    name={openPanels.step3 ? "folding" : "floating"}
                    className={`participants-step-chevron ${openPanels.step3 ? 'open' : ''}`}
                  />
                </div>

                {openPanels.step3 && (
                  <div className="participants-step-content">
                    {step3Loading ? (
                      <div className="participants-loading-container">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p className="participants-loading-text">
                          변수 맵핑 및 기준 생성 중...
                        </p>
                      </div>
                    ) : (
                      <div>
                        {/* 1. 변수 맵핑 결과 */}
                        {screenerState.mappingResult && (
                          <div className="participants-result-section">
                            <h4>🔗 변수 맵핑 결과</h4>
                            <MappingResultViewer 
                              mappingResult={screenerState.mappingResult}
                              csvInfo={screenerState.csvInfo}
                            />
                          </div>
                        )}
                        
                        {/* 2. 기준 생성 결과 */}
                        {screenerState.criteriaResult && (
                          <div className="participants-result-section">
                            <h4>📋 스코어링 기준 생성 결과</h4>
                            <CriteriaResultViewer criteriaResult={screenerState.criteriaResult} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: 최종 선별 및 유저 리스트 */}
            {(screenerState.finalSelection || screenerState.scoredData || step3Loading) && (
              <div id="participants-step4" className="participants-step-card">
                <div
                  className={`participants-step-header ${openPanels.step4 ? 'open' : ''}`}
                  onClick={() => togglePanel('step4')}
                >
                  <h2 className="participants-step-title">
                    4단계. 최종 선별 및 참여자 리스트
                  </h2>
                  <Icon24 
                    name={openPanels.step4 ? "folding" : "floating"}
                    className={`participants-step-chevron ${openPanels.step4 ? 'open' : ''}`}
                  />
                </div>

                {openPanels.step4 && (
                  <div className="participants-step-content">
                    {/* 에러 표시 */}
                    {screenerState.finalSelection?.error && (
                      <div className="participants-error-container" style={{
                        padding: '20px',
                        background: '#ffebee',
                        border: '1px solid #f44336',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        color: '#c62828'
                      }}>
                        <p style={{ margin: 0, fontWeight: 600 }}>
                          ⚠️ 오류 발생
                        </p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                          {screenerState.finalSelection.error}
                        </p>
                      </div>
                    )}
                    {/* 스피너 표시 조건: step3Loading이 true이거나 Step 4 진행 중이거나 에러가 있을 때 */}
                    {(step3Loading || (screenerState.scoredData && !screenerState.finalSelection) || screenerState.finalSelection?.error) ? (
                      <div className="participants-loading-container">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p className="participants-loading-text">
                          {step3Loading ? '3-4단계. 참여자 평가 및 최종 선별 중...' : '4단계. 최종 참여자 선별 중...'}
                        </p>
                      </div>
                    ) : screenerState.finalSelection?.error ? (
                      <div className="participants-error-container" style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#666'
                      }}>
                        <p>최종 선별 중 오류가 발생했습니다. 위의 오류 메시지를 확인해주세요.</p>
                      </div>
                    ) : (
                      <>
                        {/* 최종 선별 결과 - JSON 블록은 숨김 */}
                        {/* <div className="participants-result-section">
                          <h4>🎯 최종 선별 결과</h4>
                          <FinalSelectionViewer finalSelection={screenerState.finalSelection} />
                        </div> */}
                        
                        {/* 실제 유저 리스트 */}
                        <div className="participants-group-selection">
                      <h4>AI 추천 참여자 리스트</h4>
                      
                      {/* AI가 추천한 그룹별 참여자 표시 */}
                      {screenerState.finalSelection?.recommendations?.map((group, groupIndex) => {
                        const groupName = group.group_name;
                        
                        // AI가 추천한 참여자 ID 목록
                        const recommendedIds = group.participants.map(p => p.id);
                        
                        // participants_data에서 찾기 (백엔드에서 반환한 실제 데이터)
                        // screenerState.participantsData에 participants_data가 저장되어 있음
                        const allParticipants = screenerState.participantsData?.length > 0
                          ? screenerState.participantsData  // ⭐ 우선 사용
                          : (allParticipantsData.length > 0 
                              ? allParticipantsData 
                              : Object.values(screenerState.scoredData || {}).flat());
                        
                        return (
                          <div key={groupIndex} className="participants-group-section">
                            <h5>{groupName} ({group.participants.length}명 추천)</h5>
                            <div className="participants-list">
                              {group.participants.map((recommended, index) => {
                                // 추천 ID와 매칭되는 실제 데이터 찾기 (participants_data 우선, 없으면 scoredData에서)
                                const participant = allParticipants.find(p => {
                                  // participant_id가 ROW_1, ROW_2 형식으로 통일됨
                                  const pId = p.participant_id || p.id;
                                  const recommendedId = String(recommended.id || '');
                                  return String(pId) === recommendedId;
                                });
                                
                                if (!participant) {
                                  return null;
                                }
                                
                                // 🎯 이름 표시: _display_name 우선 사용
                                const nameColumn = screenerState.csvInfo?.name_column;
                                const hasNameColumn = screenerState.csvInfo?.has_name_column;
                                
                                let nameToDisplay;
                                
                                // ✨ 1순위: 백엔드에서 생성한 _display_name
                                if (participant._display_name && String(participant._display_name).trim()) {
                                  nameToDisplay = String(participant._display_name).trim();
                                }
                                // 2순위: name_column 직접 참조
                                else if (hasNameColumn && nameColumn && participant[nameColumn]) {
                                  nameToDisplay = String(participant[nameColumn]).trim();
                                } 
                                // 최종 fallback: participant_id 사용
                                else {
                                  nameToDisplay = `답변 ${recommended.id || participant.participant_id}`;
                                }
                                
                                // 디버깅 (첫 번째만)
                                if (index === 0 && groupIndex === 0) {
                                  console.log('🔍 이름 표시 체크:', {
                                    hasNameColumn,
                                    nameColumn,
                                    participantKeys: Object.keys(participant).slice(0, 5),
                                    displayName: participant._display_name || 'N/A',
                                    nameValue: nameColumn ? participant[nameColumn] : 'N/A',
                                    finalName: nameToDisplay
                                  });
                                }
                                
                                // 이름 확인: 이름 없는 참여자는 체크박스 비활성화
                                const participantName = nameToDisplay !== '이름 정보 없음' ? nameToDisplay : '';
                                const hasValidName = participantName && 
                                  participantName !== '' && 
                                  participantName !== '이름없음' && 
                                  participantName !== '없음';
                                
                                return (
                                <div key={index} className="participants-item">
                                  <input
                                    type="checkbox"
                                    id={`participant-${groupIndex}-${index}`}
                                    checked={selectedParticipantIds.includes(participant.participant_id || participant.id)}
                                    disabled={!hasValidName}
                                    onChange={(e) => {
                                      // 이름 없는 참여자는 선택 불가
                                      if (!hasValidName) {
                                        e.preventDefault();
                                        alert('이름이 없는 참여자는 선택할 수 없습니다.');
                                        return;
                                      }
                                      const participantId = participant.participant_id || participant.id;
                                      if (!participantId) return;

                                      if (e.target.checked) {
                                        setSelectedParticipantIds(prev => {
                                          if (prev.includes(participantId)) {
                                            return prev;
                                          }
                                          const newList = [...prev, participantId];
                                          if (newList.length >= 2) {
                                            setShowScheduleWarning(true);
                                          }
                                          return newList;
                                        });
                                      } else {
                                        setSelectedParticipantIds(prev => {
                                          const newList = prev.filter(id => id !== participantId);
                                          if (newList.length < 2) {
                                            setShowScheduleWarning(false);
                                          }
                                          return newList;
                                        });
                                      }
                                    }}
                                  />
                                  <label 
                                    htmlFor={`participant-${groupIndex}-${index}`}
                                    style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      gap: '8px'
                                    }}
                                  >
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                      <span style={{ fontSize: '16px', fontWeight: 600 }}>
                                        {nameToDisplay}
                                      </span>
                                      <span className="participants-score" style={{ color: '#666', fontSize: '14px' }}>
                                        {participant[`${groupName}_score`] || recommended.score}점
                                      </span>
                                    </div>
                                    <div style={{ color: '#999', fontSize: '13px' }}>
                                      답변ID: {recommended.id}
                                    </div>
                                    {recommended.reason && (
                                      <div style={{ color: '#666', fontSize: '13px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                                        {/* 추천 이유를 간결하게 표시 (첫 문장만) */}
                                        {(() => {
                                          const reason = recommended.reason || '';
                                          // "그룹 내 최고 점수" 같은 핵심 문장만 추출
                                          const sentences = reason.split(/[,，]/).map(s => s.trim());
                                          const coreSentence = sentences[0] || reason;
                                          // 너무 길면 50자로 자르기
                                          return coreSentence.length > 50 
                                            ? coreSentence.substring(0, 50) + '...' 
                                            : coreSentence;
                                        })()}
                                      </div>
                                    )}
                                  </label>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      
                    </div>
                      </>
                    )}
                    {/* {screenerState.finalSelection && !screenerState.finalSelection?.error && (
                      <FeedbackButton category="participants" studyId={studyId} studyName={studyName} />
                    )} */}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: 일정 생성 */}
            {finalizedParticipants.length > 0 && (
              <div id="participants-step5" className="participants-step-card">
                <div
                  className={`participants-step-header ${openPanels.step5 ? 'open' : ''}`}
                  onClick={() => togglePanel('step5')}
                >
                  <h2 className="participants-step-title">
                    5단계. 선택한 응답자 기반 일정 생성
                  </h2>
                  <Icon24 
                    name={openPanels.step5 ? "folding" : "floating"}
                    className={`participants-step-chevron ${openPanels.step5 ? 'open' : ''}`}
                  />
                </div>

                {openPanels.step5 && (
                <div className="participants-step-content">
                    {/* 참여자 리스트는 항상 표시 */}
                    <div className="participants-schedule-generation">
                        {screenerLoading === 'schedule_generation' && (
                          <div className="participants-loading-container" style={{ marginBottom: '24px' }}>
                            <FontAwesomeIcon
                              icon={faSpinner}
                              spin
                              size="2x"
                              className="participants-loading-icon"
                            />
                            <p className="participants-loading-text">
                              선택한 응답자의 일정 응답 기반 일정 맵핑 중...
                            </p>
                          </div>
                        )}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '16px'
                        }}>
                          <h4 style={{ margin: 0 }}>최종 선정 참여자 ({finalizedParticipants.length}명)</h4>
                        </div>
                        
                        {/* 그룹별 색상 범례 */}
                        {(() => {
                          const targetGroups = screenerState.planJson?.target_groups || [];
                          if (targetGroups.length === 0) return null;
                          
                          const groupColors = [
                            { bg: '#e8f0fe', border: '#1a73e8' }, // 파란색
                            { bg: '#fce8e6', border: '#d93025' }, // 빨간색
                            { bg: '#e6f4ea', border: '#137333' }, // 초록색
                            { bg: '#fef7e0', border: '#f9ab00' }, // 노란색
                            { bg: '#e8eaed', border: '#5f6368' }, // 회색
                            { bg: '#f3e5f5', border: '#9c27b0' }, // 보라색
                          ];
                          
                          return (
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '12px',
                              marginBottom: '16px',
                              padding: '12px',
                              background: '#f8f9fa',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0'
                            }}>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginRight: '8px' }}>
                                그룹 구분:
                              </span>
                              {targetGroups.map((group, idx) => {
                                const color = groupColors[idx % groupColors.length];
                                return (
                                  <div 
                                    key={idx}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      fontSize: '12px'
                                    }}
                                  >
                                    <div 
                                      style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '4px',
                                        background: color.bg,
                                        border: `2px solid ${color.border}`
                                      }}
                                    />
                                    <span style={{ color: '#333' }}>{group.name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        
                        {/* 참여자별 일정 정보 - 컴팩트 그리드 */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: '8px',
                          marginBottom: '24px'
                        }}>
                          {finalizedParticipants.map((participant, index) => {
                            // 일정 컬럼 정보 가져오기
                            const configuredScheduleColumns = screenerState.csvInfo?.schedule_columns || [];
                            const scheduleValueMap = (
                              participant._schedule_values && typeof participant._schedule_values === 'object'
                                ? participant._schedule_values
                                : {}
                            );

                            const effectiveScheduleColumns = configuredScheduleColumns.length > 0
                              ? configuredScheduleColumns
                              : Object.keys(scheduleValueMap);

                            const participantSchedule = [];

                            effectiveScheduleColumns.forEach(col => {
                              const columnValue = participant[col] || scheduleValueMap[col];
                              if (columnValue) {
                                participantSchedule.push({
                                  column: col,
                                  value: columnValue
                                });
                              }
                            });
                            
                            // 이름 가져오기 - ✨ _display_name 우선
                            const nameColumn = screenerState.csvInfo?.name_column;
                            let name = '';
                            // 1순위: _display_name
                            if (participant._display_name) {
                              name = String(participant._display_name).trim();
                            }
                            // 2순위: name_column
                            else if (nameColumn && participant[nameColumn] !== undefined && participant[nameColumn] !== null) {
                              name = String(participant[nameColumn]).trim();
                            }
                            if (!name) {
                              name = hasNameColumn ? '' : '이름 정보 없음';
                            }
                            
                            // 그룹 정보 가져오기
                            const assignedGroup = participant._assigned_group || '';
                            const targetGroups = screenerState.planJson?.target_groups || [];
                            
                            // 그룹별 색상 지정
                            const groupColors = [
                              { bg: '#e8f0fe', border: '#1a73e8', hoverBg: '#d2e3fc' }, // 파란색
                              { bg: '#fce8e6', border: '#d93025', hoverBg: '#f9d1cd' }, // 빨간색
                              { bg: '#e6f4ea', border: '#137333', hoverBg: '#d3eadb' }, // 초록색
                              { bg: '#fef7e0', border: '#f9ab00', hoverBg: '#fdedc1' }, // 노란색
                              { bg: '#e8eaed', border: '#5f6368', hoverBg: '#dadce0' }, // 회색
                              { bg: '#f3e5f5', border: '#9c27b0', hoverBg: '#e1bee7' }, // 보라색
                            ];
                            
                            // 그룹 인덱스 찾기
                            const groupIndex = targetGroups.findIndex(g => g.name === assignedGroup);
                            const groupColor = groupIndex >= 0 ? groupColors[groupIndex % groupColors.length] : { bg: '#fafafa', border: '#e0e0e0', hoverBg: '#f0f0f0' };
                            
                            // 일정 정보 정제: 날짜가 포함된 항목만 추출
                            const formattedSchedules = participantSchedule
                              .map(sched => {
                                const value = sched.value;
                                const cleaned = value.split(':').pop().trim();
                                return cleaned;
                              })
                              .filter(sched => {
                                const hasDatePattern = /\d{1,2}\/\d{1,2}/.test(sched);
                                return hasDatePattern && sched !== '일정 정보 없음';
                              });
                            
                            return (
                              <div 
                                key={index} 
                                style={{ 
                                  padding: '10px', 
                                  border: `1px solid ${groupColor.border}`, 
                                  borderRadius: '6px',
                                  background: groupColor.bg,
                                  fontSize: '13px',
                                  transition: 'all 0.2s ease',
                                  cursor: 'default',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = groupColor.hoverBg;
                                  e.currentTarget.style.borderColor = groupColor.border;
                                  if (e.currentTarget.querySelector('.schedule-tooltip')) {
                                    e.currentTarget.querySelector('.schedule-tooltip').style.display = 'block';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = groupColor.bg;
                                  e.currentTarget.style.borderColor = groupColor.border;
                                  if (e.currentTarget.querySelector('.schedule-tooltip')) {
                                    e.currentTarget.querySelector('.schedule-tooltip').style.display = 'none';
                                  }
                                }}
                              >
                                <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                                  {name}
                                </div>
                                {formattedSchedules.length > 0 ? (
                                  <>
                                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>
                                      {formattedSchedules.length}개 일정
                                    </div>
                                    {/* 호버 시 전체 일정 표시 */}
                                    <div 
                                      className="schedule-tooltip"
                                      style={{
                                        display: 'none',
                                        position: 'absolute',
                                        bottom: '100%',
                                        left: '0',
                                        right: '0',
                                        marginBottom: '8px',
                                        padding: '8px',
                                        background: '#333',
                                        color: '#fff',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        zIndex: 1000,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                      }}
                                    >
                                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>희망 일정:</div>
                                      {formattedSchedules.map((sched, idx) => (
                                        <div key={idx}>{sched}</div>
                                      ))}
                                    </div>
                                  </>
                                ) : (
                                  <div style={{ color: '#bbb', fontSize: '11px' }}>
                                    {effectiveScheduleColumns.length === 0 ? '일정 정보 없음' : '일정 없음'}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* 달력 UI */}
                        <div style={{ marginTop: '32px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '16px' 
                          }}>
                            <h5 style={{ margin: 0 }}>📅 달력으로 보기</h5>
                            {currentSchedule && (
                              <button
                                className="btn btn-primary"
                                onClick={handleSaveSchedule}
                                disabled={isSaving}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px'
                                }}
                              >
                                <FontAwesomeIcon icon={faFloppyDisk} />
                                {isSaving ? '저장 중...' : '선별된 참여자 및 일정 저장'}
                              </button>
                            )}
                          </div>
            <SimpleCalendar
              participants={finalizedParticipants}
              scheduleColumns={screenerState.csvInfo?.schedule_columns || []}
              nameColumn={screenerState.csvInfo?.name_column || '이름'}
              hasNameColumn={hasNameColumn}
              targetGroups={screenerState.planJson?.target_groups || []}
              updateTrigger={scheduleUpdateTrigger}
              studyId={studyId}
              onScheduleReady={setCurrentSchedule}
            />
                        </div>

                        {/* 경고 카드 - 달력 바로 아래, Step 5 카드 안에 표시 */}
                        {currentSchedule?.validationData && (
                          (currentSchedule.validationData.unassigned_count > 0 || 
                           currentSchedule.validationData.missing_count > 0) && (
                            <div className="participants-step-container participants-warning-card">
                              <div>
                                <div className="participants-warning-title">
                                  ⚠️ 경고: 일부 참여자({currentSchedule.validationData.unassigned_count + currentSchedule.validationData.missing_count}명)가 일정에 배정되지 않았습니다.
                                </div>
                                <div className="participants-warning-content">
                                  {(() => {
                                    const { unassigned_count, missing_count, unassigned_participants, missing_participants } = currentSchedule.validationData;
                                    const parts = [];
                                    
                                    if (unassigned_count > 0 && unassigned_participants?.length > 0) {
                                      parts.push(
                                        <div key="unassigned" className="participants-warning-section">
                                          <div className="participants-warning-section-title">
                                            [일정 조율 불가] {unassigned_participants.length}명:
                                          </div>
                                          <div className="participants-warning-section-list">
                                            {unassigned_participants.join(', ')}
                                          </div>
                                          <div className="participants-warning-section-description">
                                            → 가능한 일정이 다른 참여자들과 맞지 않아 배정되지 않았습니다.
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    if (missing_count > 0 && missing_participants?.length > 0) {
                                      parts.push(
                                        <div key="missing" className="participants-warning-section">
                                          <div className="participants-warning-section-title">
                                            [일정 정보 누락] {missing_participants.length}명:
                                          </div>
                                          <div className="participants-warning-section-list">
                                            {missing_participants.join(', ')}
                                          </div>
                                          <div className="participants-warning-section-description">
                                            → 일정 데이터가 없거나 배정 과정에서 누락되었습니다.
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    return parts;
                                  })()}
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 플로팅 액션 버튼 1 - 최적의 조사 참여자 찾기 (1,2단계 완료 시) */}
        {screenerState.planJson && analysisStatus === 'completed' && !step3Loading && !screenerState.mappingResult && (
          <div 
            className="participants-floating-action" 
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 96,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 1000
            }}
          >
            <button 
              className="participants-fab"
              style={{
                pointerEvents: 'auto',
                padding: '16px 28px',
                fontSize: '16px',
                borderRadius: '12px'
              }}
              onClick={async () => {
                // 3단계를 열고, 1-2단계는 닫기
                setOpenPanels(prev => ({ ...prev, step1: false, step2: false, step3: true, step4: false }));
                
                try {
                  // 필요한 데이터를 전달하여 함수 호출
                  await handleFindOptimalParticipants(
                    studyId,
                    screenerState.csvContent,
                    screenerState.csvInfo,
                    screenerState.planJson,
                    screenerState.sincerityRules
                  );
                  
                  // 4단계 완료 후에도 3단계는 열어두기
                  setOpenPanels(prev => ({ ...prev, step3: true, step4: true }));
                } catch (error) {
                  console.error('[ERROR] 자동 실행 중 오류:', error);
                  alert('자동 실행 중 오류가 발생했습니다: ' + error.message);
                }
                
                // 3단계로 스크롤
                setTimeout(() => {
                  if (step3Ref && step3Ref.current) {
                    step3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 50);
              }}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              <span style={{ marginLeft: 8 }}>최적의 조사 참여자 찾기</span>
            </button>
          </div>
        )}

        {/* 플로팅 액션 버튼 2 - 일정 생성하기 (4단계 완료 후에만 표시, 오류 없을 때만, Step 5가 열려있지 않을 때만) */}
        {openPanels.step4 && !openPanels.step5 && !step3Loading && screenerState.finalSelection && (
          <div 
            className="participants-floating-action" 
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 96,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pointerEvents: 'none',
              zIndex: 1000,
              gap: '8px'
            }}
          >
            {/* 에러 메시지 표시 (오류가 있을 때) */}
            {finalizeError && (
              <div style={{ 
                padding: '12px 16px', 
                fontSize: '13px', 
                color: '#c62828',
                background: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                maxWidth: '90%',
                textAlign: 'center',
                pointerEvents: 'auto',
                marginBottom: '8px'
              }}>
                ⚠️ 스마트 선정 중 오류가 발생했습니다. 다시 시도해주세요.
              </div>
            )}
            
            {/* 경고 문구 - 2명 이상 선택 시 표시 (Step 5가 열려있지 않을 때만) */}
            {selectedParticipantIds.length > 1 && showScheduleWarning && !openPanels.step5 && (
              <div style={{ 
                padding: '8px 16px', 
                fontSize: '12px', 
                color: '#856404',
                background: '#fff3cd',
                borderRadius: '4px',
                maxWidth: '90%',
                textAlign: 'center',
                pointerEvents: 'auto',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px'
              }}>
                <span>⚠️ 선택한 인원의 가능 일정이 제한적인 경우 일정 생성에서 제외될 수 있습니다</span>
                <button
                  onClick={() => setShowScheduleWarning(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#856404',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '0',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  ×
                </button>
              </div>
            )}
            
            {/* 총 필요 인원 수 계산
            {screenerState.planJson?.target_groups && (() => {
              const totalTarget = screenerState.planJson.target_groups.reduce((sum, g) => sum + (g.targetCount || 0), 0);
              const remaining = totalTarget - selectedParticipantIds.length;
              
              return remaining > 0 ? (
                <div style={{ 
                  padding: '8px 16px', 
                  fontSize: '12px', 
                  color: '#0c5460',
                  background: '#d1ecf1',
                  borderRadius: '4px',
                  maxWidth: '90%',
                  textAlign: 'center',
                  pointerEvents: 'auto',
                  marginBottom: '8px'
                }}>
                  ℹ️ 총 필요 인원: {totalTarget}명 / 선택: {selectedParticipantIds.length}명 / 부족: {remaining}명
                </div>
              ) : null;
            })()}
             */}
            {/* [TODO: 나중에 다시 살릴 코드] 기존 버튼: 선택된 인원만으로 일정 생성 */}
            {/* 
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: '90%'
            }}>
              <button 
                className="participants-fab"
                style={{
                  pointerEvents: 'auto',
                  padding: '16px 28px',
                  fontSize: '16px',
                  borderRadius: '12px',
                  width: '100%'
                }}
                onClick={() => {
                  // Step 5 열기 및 달력 업데이트 트리거
                  setOpenPanels(prev => ({
                    ...prev,
                    step3: false,
                    step4: false,
                    step5: true
                  }));
                  
                  // 달력 업데이트 트리거 증가 (버튼 클릭 시에만)
                  setScheduleUpdateTrigger(prev => prev + 1);
                  
                  setTimeout(() => {
                    const step5Element = document.getElementById('participants-step5');
                    if (step5Element) {
                      step5Element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
              >
                <FontAwesomeIcon icon={faCog} />
                <span style={{ marginLeft: 8 }}>최적의 조사일정 생성하기 ({finalizedParticipants.length}명 확정)</span>
              </button>
            </div>
            */}
            
            {/* 스마트 선정 및 일정 생성 버튼 */}
            {screenerState.planJson?.target_groups && (() => {
              const totalTarget = screenerState.planJson.target_groups.reduce((sum, g) => sum + (g.targetCount || 0), 0);
              const remaining = Math.max(totalTarget - selectedParticipantIds.length, 0);

              return (
                <button 
                  className="participants-fab"
                  disabled={finalizeLoading}
                  style={{
                    pointerEvents: finalizeLoading ? 'none' : 'auto',
                    padding: '16px 28px',
                    fontSize: '16px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #7C9CE8 0%, #9BB5F0 100%) !important',
                    backgroundColor: 'transparent !important',
                    border: 'none !important',
                    color: 'white !important',
                    cursor: finalizeLoading ? 'not-allowed' : 'pointer',
                    opacity: 1
                  }}
                  onClick={handleSmartSelectionAndSchedule}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} spin={finalizeLoading} />
                  <span style={{ marginLeft: 8 }}>
                    {finalizeLoading 
                      ? '최종 선정 정리 중...' 
                      : selectedParticipantIds.length > 0
                        ? `최종 선정 및 일정 준비하기 (${selectedParticipantIds.length}명 선택 + ${remaining}명 자동 추천)`
                        : `최종 선정 및 일정 준비하기 (${remaining}명 자동 추천)`}
                  </span>
                </button>
              );
            })()}
          </div>
        )}
      </div>
      
      {/* 토스트 팝업 */}
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ ...toast, isOpen: false })}
        autoClose={true}
        autoCloseDelay={3000}
      />
    </WorkspaceLayout>
  );
};

export default ParticipantsPicker;
